<template>
  <div class="dashboard">
    <MobileHeader @toggle-sidebar="toggleSidebar" />
    
    <Sidebar 
      :isOpen="sidebarOpen" 
      :isMobile="isMobile" 
      @close="closeSidebar"
    />
    
    <main class="main-content" :class="{ 'main-content-mobile': isMobile }">
      <div class="chat-layout">
        <!-- Mobile Category Select -->
        <div v-if="isMobile" class="mobile-category-select">
          <select 
            v-model="selectedCategoryId" 
            @change="onCategoryChange"
            class="category-select"
          >
            <option value="">Изберете категория...</option>
            <option 
              v-for="category in categoriesList" 
              :key="category.id" 
              :value="category.id"
            >
              {{ category.name }} ({{ getMessageCount(category) }} съобщения)
            </option>
          </select>
        </div>

        <!-- Categories Sidebar -->
        <aside v-if="!isMobile" class="categories-sidebar">
          <div class="categories-header">
            <h3>Чат категории</h3>
            <router-link to="/categories" class="btn btn-sm btn-primary">
              <i class="fas fa-cog"></i>
              Управление
            </router-link>
          </div>
          
          <div v-if="loadingCategories" class="loading-small">
            <i class="fas fa-spinner fa-spin"></i>
            Зареждане...
          </div>
          
          <div v-else-if="categoriesList.length === 0" class="no-categories">
            <p>Няма налични категории</p>
            <router-link to="/categories" class="btn btn-sm btn-primary">
              Създай категория
            </router-link>
          </div>
          
          <ul v-else class="categories-list">
            <li
              v-for="category in categoriesList"
              :key="category.id"
              class="category-item"
              :class="{ active: selectedCategory?.id === category.id }"
              @click="selectCategory(category)"
            >
              <div class="category-info">
                <h4>{{ category.name }}</h4>
                <span class="message-count">{{ getMessageCount(category) }} съобщения</span>
              </div>
              <div v-if="category.lastMessage" class="last-message">
                <small>{{ formatDate(category.lastMessage.timestamp) }}</small>
              </div>
            </li>
          </ul>
        </aside>
        
        <!-- Chat Area -->
        <div class="chat-area">
          <div v-if="!selectedCategory" class="chat-placeholder">
            <i class="fas fa-comments"></i>
            <h3>Изберете категория за започване на чат</h3>
            <p>Изберете категория от страничната лента за преглед и публикуване на съобщения</p>
          </div>
          
          <div v-else class="chat-container">
            <!-- Chat Header -->
            <header class="chat-header">
              <div class="chat-title">
                <h2>{{ selectedCategory.name }}</h2>
                <p v-if="selectedCategory.description">{{ selectedCategory.description }}</p>
              </div>
              <div class="chat-stats">
                <span class="message-count">
                  <i class="fas fa-comments"></i>
                  {{ getMessageCount(selectedCategory) }} съобщения
                </span>
              </div>
            </header>
            
            <!-- Messages Area -->
            <div class="messages-container" ref="messagesContainer">
              <div v-if="loadingMessages" class="loading">
                <i class="fas fa-spinner fa-spin"></i>
                Зареждане на съобщения...
              </div>
              
              <div v-else-if="messagesList.length === 0" class="no-messages">
                <i class="fas fa-comment-slash"></i>
                <p>Няма съобщения в тази категория</p>
                <p>Бъдете първия, който ще напише съобщение!</p>
              </div>
              
              <div v-else class="messages-list">
                <div
                  v-for="message in messagesList"
                  :key="message.id"
                  class="message-wrapper"
                  :class="{ 
                    'own-message-wrapper': message.author?.email === currentUserEmail,
                    'scheduled-message-wrapper': message.isScheduled || (message.scheduled && message.timestamp > Date.now())
                  }"
                >
                  <div class="message-bubble" :class="{ 'scheduled-message': message.isScheduled || (message.scheduled && message.timestamp > Date.now()) }">
                    <button
                      @click="showDeleteDialog(message)"
                      class="delete-message-btn"
                      title="Изтрий съобщение"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                    <div class="message-author">{{ message.author?.name || message.author || 'Анонимен' }}</div>
                    <div class="message-content" v-html="message.content"></div>
                    <div v-if="message.isScheduled || (message.scheduled && message.timestamp > Date.now())" class="scheduled-indicator">
                      <i class="fas fa-clock"></i>
                      Планирано за {{ formatScheduledDate(message.timestamp) }}
                    </div>
                  </div>
                  <div class="message-time">{{ formatDate(message.timestamp) }}</div>
                </div>
              </div>
            </div>
            
            <!-- Message Composer -->
            <div class="message-composer">
              <div class="composer-header">
                <div>
                  <h4>Напишете съобщение</h4>
                  <div class="message-info">
                    <small>Съобщенията се публикуват като: {{ currentUserName }}</small>
                  </div>
                </div>

                <div class="composer-actions">
                  <button @click="clearEditor" class="btn btn-sm btn-secondary">
                    <i class="fas fa-eraser"></i>
                    Изчисти
                  </button>
                </div>
              </div>

              
              
              <div class="editor-container">
                <QuillEditor
                  ref="quillEditor"
                  v-model:content="messageContent"
                  content-type="html"
                  :options="editorOptions"
                  placeholder="Напишете вашето съобщение тук..."
                  @ready="onEditorReady"
                />
              </div>
              
              <div class="composer-footer">                  <div class="composer-controls">
                    
                    
                    <div class="schedule-controls">
                      <div class="schedule-input-group">
                        <label>
                          <i class="fas fa-clock"></i>
                          Планирайте за дата и час (незадължително):
                        </label>
                        <VueDatePicker
                          v-model="scheduledDateTime"
                          :min-date="new Date()"
                          :enable-time-picker="true"
                          :is-24="true"
                          format="dd.MM.yyyy HH:mm"
                          locale="bg"
                          placeholder="Изберете дата и час за планиране"
                          :clearable="true"
                          auto-apply
                          class="datepicker-input"
                        />
                        <small v-if="scheduledDateTime" class="schedule-note">
                          <i class="fas fa-info-circle"></i>
                          Съобщението ще бъде публикувано на {{ formatScheduledDate(scheduledDateTime.getTime()) }}
                        </small>
                      </div>
                    </div>
                  </div>                  <button
                    @click="sendMessage"
                    class="btn btn-primary"
                    :disabled="!messageContent.trim() || sending"
                  >
                    <i class="fas fa-paper-plane"></i>
                    {{ sending ? 'Изпращане...' : (scheduledDateTime ? 'Планирай съобщение' : 'Изпрати съобщение') }}
                  </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="cancelDelete">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fas fa-exclamation-triangle"></i>
            Изтриване на съобщение
          </h3>
        </div>
        <div class="modal-body">
          <p>Сигурни ли сте, че искате да изтриете това съобщение?</p>
          <div v-if="messageToDelete" class="message-preview">
            <strong>{{ messageToDelete.author?.name || messageToDelete.author || 'Анонимен' }}:</strong>
            <span v-html="messageToDelete.content"></span>
          </div>
          <p><small class="text-muted">Това действие не може да бъде отменено.</small></p>
        </div>
        <div class="modal-footer">
          <button @click="cancelDelete" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Отказ
          </button>
          <button @click="confirmDelete" class="btn btn-danger">
            <i class="fas fa-trash"></i>
            Изтрий
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth, database } from '../firebase'
import { ref as dbRef, onValue, push, update, remove, query, orderByChild, equalTo } from 'firebase/database'
import moment from 'moment'
import Sidebar from './Sidebar.vue'
import MobileHeader from './MobileHeader.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: 'ChatPage',
  components: {
    Sidebar,
    MobileHeader,
    VueDatePicker
  },
  data() {
    return {
      loadingCategories: true,
      loadingMessages: false,
      sending: false,
      categories: {},
      users: {},
      scheduledMessages: {},
      selectedCategory: null,
      selectedCategoryId: '',
      messageContent: '',
      scheduledDateTime: null,
      sidebarOpen: false,
      isMobile: false,
      showDeleteModal: false,
      messageToDelete: null,
      categoriesUnsubscribe: null,
      usersUnsubscribe: null,
      scheduledMessagesUnsubscribe: null,
      editorOptions: {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
          ]
        }
      }
    }
  },
  computed: {
    currentUserEmail() {
      return auth.currentUser?.email || ''
    },
    currentUserName() {
      const email = this.currentUserEmail
      const user = Object.values(this.users).find(u => u.email === email)
      return user?.name || email
    },
    categoriesList() {
      return Object.entries(this.categories)
        .map(([id, category]) => ({ id, ...category }))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },
    messagesList() {
      const messages = []
      
      // Add regular (published) messages
      if (this.selectedCategory?.messages) {
        const regularMessages = Object.entries(this.selectedCategory.messages)
          .map(([id, message]) => ({ id, ...message, isScheduled: false }))
        messages.push(...regularMessages)
      }
      
      // Add scheduled messages
      if (this.scheduledMessages) {
        const scheduledMessages = Object.entries(this.scheduledMessages)
          .map(([id, message]) => ({ id, ...message, isScheduled: true, scheduled: true }))
        messages.push(...scheduledMessages)
      }
      
      // Sort all messages by timestamp
      return messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    },
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen
    },
    closeSidebar() {
      this.sidebarOpen = false
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const now = moment()
      const messageTime = moment(timestamp)
      
      if (now.diff(messageTime, 'days') === 0) {
        return messageTime.format('HH:mm')
      } else if (now.diff(messageTime, 'days') === 1) {
        return 'Yesterday ' + messageTime.format('HH:mm')
      } else if (now.diff(messageTime, 'days') < 7) {
        return messageTime.format('ddd HH:mm')
      } else {
        return messageTime.format('MMM DD, HH:mm')
      }
    },
    formatScheduledDate(timestamp) {
      if (!timestamp) return ''
      return moment(timestamp).format('DD.MM.YYYY в HH:mm')
    },
    getMessageCount(category) {
      if (!category.messages) return 0
      return Object.keys(category.messages).length
    },
    async selectCategory(category) {
      if (this.selectedCategory?.id === category.id) return
      
      this.loadingMessages = true
      this.selectedCategory = category
      this.selectedCategoryId = category.id
      
      // Update URL
      if (this.$route.params.categoryId !== category.id) {
        this.$router.push(`/chat/${category.id}`)
      }

      this.listenToScheduledMessages(category.id)
      this.loadingMessages = false

      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    listenToScheduledMessages(categoryId) {
      if (this.scheduledMessagesUnsubscribe) {
        this.scheduledMessagesUnsubscribe()
      }

      this.scheduledMessages = {}
      const scheduledMessagesQuery = query(
        dbRef(database, 'scheduledMessages'),
        orderByChild('categoryId'),
        equalTo(categoryId)
      )

      this.scheduledMessagesUnsubscribe = onValue(scheduledMessagesQuery, (snapshot) => {
        this.scheduledMessages = snapshot.val() || {}
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      })
    },
    onCategoryChange() {
      if (this.selectedCategoryId) {
        const category = this.categoriesList.find(cat => cat.id === this.selectedCategoryId)
        if (category) {
          this.selectCategory(category)
        }
      } else {
        if (this.scheduledMessagesUnsubscribe) {
          this.scheduledMessagesUnsubscribe()
          this.scheduledMessagesUnsubscribe = null
        }
        this.scheduledMessages = {}
        this.selectedCategory = null
        this.$router.push('/chat')
      }
    },
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        const container = this.$refs.messagesContainer
        container.scrollTop = container.scrollHeight
      }
    },
    async sendMessage() {
      if (!this.messageContent.trim() || !this.selectedCategory || this.sending) return
      
      this.sending = true
      
      try {
        let timestamp = Date.now()
        const isScheduled = !!this.scheduledDateTime
        
        // Handle scheduled messages
        if (isScheduled) {
          timestamp = this.scheduledDateTime.getTime()
          
          // Validate that scheduled time is in the future
          if (timestamp <= Date.now()) {
            alert('Планираният час трябва да бъде в бъдещето!')
            this.sending = false
            return
          }
        }

        const messageData = {
          content: this.messageContent,
          author: {
            email: this.currentUserEmail,
            name: this.currentUserName
          },
          timestamp: timestamp,
          scheduled: isScheduled,
          isFromAdmin: true // Mark as admin message
        }

        if (isScheduled) {
          // Add to scheduled messages collection (Firebase Functions will handle publishing)
          await push(dbRef(database, 'scheduledMessages'), {
            ...messageData,
            categoryId: this.selectedCategory.id
          })
        } else {
          // Publish the message and update the category summary atomically.
          const messageRef = push(dbRef(database, `categories/${this.selectedCategory.id}/messages`))
          await update(dbRef(database), {
            [`categories/${this.selectedCategory.id}/messages/${messageRef.key}`]: messageData,
            [`categories/${this.selectedCategory.id}/lastMessage`]: messageData
          })
        }

        // Clear editor and reset scheduling
        this.clearEditor()
        this.scheduledDateTime = null
        
        // Scroll to bottom only for immediate messages
        if (!isScheduled) {
          this.$nextTick(() => {
            this.scrollToBottom()
          })
        }
        
      } catch (error) {
        console.error('Error sending message:', error)
        alert('Грешка при изпращане на съобщението!')
      } finally {
        this.sending = false
      }
    },
    showDeleteDialog(message) {
      this.messageToDelete = message
      this.showDeleteModal = true
    },
    cancelDelete() {
      this.showDeleteModal = false
      this.messageToDelete = null
    },
    async confirmDelete() {
      if (!this.messageToDelete) return
      
      try {
        // Determine if this is a scheduled message or regular message
        const isScheduledMessage = this.messageToDelete.isScheduled || (this.messageToDelete.scheduled && this.messageToDelete.timestamp > Date.now())
        const messagePath = isScheduledMessage
          ? `scheduledMessages/${this.messageToDelete.id}`
          : `categories/${this.selectedCategory.id}/messages/${this.messageToDelete.id}`

        await remove(dbRef(database, messagePath))
        
        console.log(`${isScheduledMessage ? 'Scheduled' : 'Regular'} message deleted successfully`)
        this.showDeleteModal = false
        this.messageToDelete = null
      } catch (error) {
        console.error('Error deleting message:', error)
        alert('Грешка при изтриване на съобщението!')
      }
    },
    async deleteMessage(message) {
      if (!confirm('Сигурни ли сте, че искате да изтриете това съобщение?')) return
      
      try {
        // Remove message from database
        await remove(dbRef(database, `categories/${this.selectedCategory.id}/messages/${message.id}`))
        
        console.log('Message deleted successfully')
      } catch (error) {
        console.error('Error deleting message:', error)
        alert('Грешка при изтриване на съобщението!')
      }
    },
    clearEditor() {
      this.messageContent = ''
      if (this.$refs.quillEditor) {
        this.$refs.quillEditor.setHTML('')
      }
    },
    onEditorReady(quill) {
      // Editor is ready
      console.log('Quill editor ready:', quill)
    },
    loadCategories() {
      const categoriesRef = dbRef(database, 'categories')
      this.categoriesUnsubscribe = onValue(categoriesRef, (snapshot) => {
        this.categories = snapshot.val() || {}
        this.loadingCategories = false
        
        // If there's a category ID in the route, select it
        const categoryId = this.$route.params.categoryId
        if (categoryId && this.categories[categoryId]) {
          const category = { id: categoryId, ...this.categories[categoryId] }
          if (this.selectedCategory?.id === categoryId) {
            this.selectedCategory = category
          } else {
            this.selectCategory(category)
          }
          this.selectedCategoryId = categoryId
        }
      })
    },
    loadUsers() {
      const usersRef = dbRef(database, 'users')
      this.usersUnsubscribe = onValue(usersRef, (snapshot) => {
        this.users = snapshot.val() || {}
      })
    }
  },
  watch: {
    '$route.params.categoryId'(newCategoryId) {
      if (newCategoryId && this.categories[newCategoryId]) {
        this.selectCategory({ id: newCategoryId, ...this.categories[newCategoryId] })
        this.selectedCategoryId = newCategoryId
      } else {
        this.selectedCategoryId = ''
        this.selectedCategory = null
        this.scheduledMessages = {}
        if (this.scheduledMessagesUnsubscribe) {
          this.scheduledMessagesUnsubscribe()
          this.scheduledMessagesUnsubscribe = null
        }
      }
    }
  },
  mounted() {
    this.loadCategories()
    this.loadUsers()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    if (this.categoriesUnsubscribe) this.categoriesUnsubscribe()
    if (this.usersUnsubscribe) this.usersUnsubscribe()
    if (this.scheduledMessagesUnsubscribe) this.scheduledMessagesUnsubscribe()
  }
}
</script>

<style scoped>
.dashboard {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
}

.mobile-header {
  display: none;
  background: #1D687B;
  color: white;
  padding: 16px 20px;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
}

.mobile-menu-btn {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.mobile-menu-btn:hover {
  background-color: rgba(121, 218, 242, 0.2);
}

.mobile-header h1 {
  font-size: 18px;
  margin: 0;
  font-weight: 600;
}

.mobile-spacer {
  width: 36px; /* Same width as menu button for centering */
}

.main-content {
  flex: 1;
  margin-left: 260px;
  background: #F8F8F8;
  display: flex;
  flex-direction: column;
}

.main-content-mobile {
  margin-left: 0;
}

.chat-layout {
  display: flex;
  height: 100vh;
}

.categories-sidebar {
  width: 320px;
  background: white;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.categories-header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.categories-header h3 {
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}

.loading-small {
  padding: 20px;
  text-align: center;
  color: #6b7280;
}

.no-categories {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

.categories-list {
  flex: 1;
  overflow-y: auto;
  list-style: none;
  padding: 0;
}

.category-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.category-item:hover {
  background-color: #f9fafb;
}

.category-item.active {
  background-color: #eff6ff;
  border-right: 3px solid #1D687B;
}

.category-info h4 {
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.message-count {
  color: #6b7280;
  font-size: 12px;
}

.last-message {
  margin-top: 8px;
}

.last-message small {
  color: #9ca3af;
  font-size: 11px;
}

.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chat-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  text-align: center;
}

.chat-placeholder i {
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.3;
}

.chat-placeholder h3 {
  font-size: 24px;
  margin-bottom: 8px;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-title h2 {
  color: #1f2937;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 4px;
}

.chat-title p {
  color: #6b7280;
  font-size: 14px;
}

.chat-stats {
  color: #6b7280;
  font-size: 14px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  background: #f9fafb;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  text-align: center;
}

.no-messages i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 70%;
}

.own-message-wrapper {
  align-items: flex-end;
  align-self: flex-end;
}

.message-bubble {
  background: white;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  position: relative;
}

.delete-message-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(220, 38, 38, 0.9);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.delete-message-btn:hover {
  background: rgba(185, 28, 28, 1);
  transform: scale(1.1);
}

/* Show delete button on hover for desktop */
.message-wrapper:hover .delete-message-btn {
  opacity: 1;
}

/* Always show delete button on mobile */
@media (max-width: 768px) {
  .delete-message-btn {
    opacity: 1;
  }
}

.own-message-wrapper .message-bubble {
  background: #1D687B;
  color: white;
  border: 1px solid #1D687B;
}

.message-author {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #374151;
}

.own-message-wrapper .message-author {
  color: #dbeafe;
}

.message-content {
  line-height: 1.5;
  font-size: 14px;
  word-wrap: break-word;
}

.message-content :deep(ul),
.message-content :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.message-content :deep(li) {
  margin: 4px 0;
}

.message-content :deep(p) {
  margin: 8px 0;
}

.message-content :deep(p:first-child) {
  margin-top: 0;
}

.message-content :deep(p:last-child) {
  margin-bottom: 0;
}

.own-message-wrapper .message-content {
  color: white;
}

.scheduled-message-wrapper .message-bubble {
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: white;
  border: 1px solid #f59e0b;
  position: relative;
}

.scheduled-message-wrapper .message-bubble::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  border-radius: 14px;
  z-index: -1;
  opacity: 0.3;
}

.scheduled-message {
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3) !important;
}

.scheduled-indicator {
  margin-top: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.scheduled-indicator i {
  font-size: 11px;
}

.message-time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  margin-left: 8px;
  margin-right: 8px;
}

.message-composer {
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 20px 24px;
}

.composer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.composer-header h4 {
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.editor-container {
  margin-bottom: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.composer-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
}

.composer-controls {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.schedule-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schedule-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.schedule-input-group label {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.schedule-input-group label i {
  color: #1D687B;
}

.schedule-note {
  color: #1D687B;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(29, 104, 123, 0.1);
  border-radius: 6px;
  border-left: 3px solid #1D687B;
}

.schedule-note i {
  color: #1D687B;
}

/* VueDatePicker Customization */
.datepicker-input {
  width: 100%;
}

:deep(.dp__input) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  width: 100%;
}

:deep(.dp__input:focus) {
  outline: none;
  border-color: #1D687B;
  box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
}

:deep(.dp__theme_light) {
  --dp-background-color: #ffffff;
  --dp-text-color: #374151;
  --dp-hover-color: #f3f4f6;
  --dp-hover-text-color: #374151;
  --dp-border-color: #d1d5db;
  --dp-menu-border-color: #d1d5db;
  --dp-primary-color: #1D687B;
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #f3f4f6;
  --dp-border-color-hover: #1D687B;
}

.message-info {
  color: #6b7280;
}

.btn-sm {
  padding: 10px 14px;
  font-size: 14px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Quill Editor Customization */
:deep(.ql-editor) {
  min-height: 120px;
  font-size: 14px;
  line-height: 1.5;
}

:deep(.ql-toolbar) {
  border-bottom: 1px solid #d1d5db;
}

:deep(.ql-container) {
  border: none;
}

@media (max-width: 768px) {
  .sidebar {
    width: 100%;
    position: relative;
    height: auto;
  }
  
  .main-content {
    margin-left: 0;
  }
  
  .mobile-header {
    display: flex;
  }
  
  .main-content {
    margin-left: 0;
  }
  
  .chat-layout {
    flex-direction: column;
    height: calc(100vh - 64px); /* Account for mobile header */
  }

  .mobile-category-select {
    padding: 16px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }

  .category-select {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 16px;
    color: #374151;
  }

  .category-select:focus {
    outline: none;
    border-color: #1D687B;
    box-shadow: 0 0 0 3px rgba(29, 104, 123, 0.1);
  }
  
  .categories-sidebar {
    width: 100%;
    height: 200px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .message-wrapper {
    max-width: 90%;
  }
  
  .composer-controls {
    flex-direction: column;
    gap: 12px;
  }
  
  .composer-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  
  .datepicker-input :deep(.dp__input) {
    min-width: auto;
    width: 100%;
  }
  
  .categories-list {
    flex-direction: row;
    overflow-x: auto;
    padding: 0 16px;
  }
  
  .category-item {
    min-width: 200px;
    margin-right: 12px;
  }
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-body {
  padding: 20px 24px;
}

.modal-body p {
  margin: 0 0 16px 0;
  color: #374151;
  line-height: 1.5;
}

.message-preview {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  font-size: 14px;
  line-height: 1.4;
}

.message-preview strong {
  color: #1D687B;
  display: block;
  margin-bottom: 4px;
}

.text-muted {
  color: #6b7280;
  font-size: 13px;
}

.modal-footer {
  padding: 16px 24px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-danger {
  background: #dc2626;
  color: white;
  border: 1px solid #dc2626;
}

.btn-danger:hover {
  background: #b91c1c;
  border-color: #b91c1c;
}

.btn-secondary {
  background: #6b7280;
  color: white;
  border: 1px solid #6b7280;
}

.btn-secondary:hover {
  background: #4b5563;
  border-color: #4b5563;
}
</style>
